import { FileSourceEnum } from '@systemeio/file-manager'
import { MailingId } from 'modules/mailing/types/mailing-interface'
import { getFilteredAttachmentsList } from 'modules/profile/mailing-settings/utils/file-utils'
import React from 'react'
import { useAttachmentsPolicy } from 'shared/components/dashboard-mail-editor/hooks/use-attachments-policy'
import {
  AttachmentFileInterface,
  UnsavedAttachmentFileInterface,
} from 'shared/components/dashboard-mail-editor/types/email-interface'
import { concatAttachments } from 'shared/components/dashboard-mail-editor/utils/concat-attachments'
import { DisclosureBox } from 'shared/components/disclosure-box'
import { FieldErrorAndDescription } from 'shared/components/form/field-error-and-description'
import { SectionMessage } from 'shared/components/section-message'
import { useLocoTranslation } from 'shared/hooks/use-loco-translation'
import { twJoin } from 'tailwind-merge'
import { DashboardMailEditorCommonProps } from '../types/common'
import { FileUploaderWithListedFiles } from 'shared/components/file-uploader/file-uploader-with-listed-files'

export interface AttachmentsBlockProps
  extends Pick<DashboardMailEditorCommonProps, 'isPreFetching' | 'inModal' | 'disabled'> {
  attachmentsTempState: (AttachmentFileInterface | UnsavedAttachmentFileInterface)[]
  setAttachmentsTempState: React.Dispatch<
    React.SetStateAction<(AttachmentFileInterface | UnsavedAttachmentFileInterface)[]>
  >
  mailingId?: MailingId
  errors: string | undefined
}

const AttachmentsBlock = ({
  isPreFetching,
  attachmentsTempState,
  setAttachmentsTempState,
  inModal,
  mailingId,
  errors,
}: AttachmentsBlockProps) => {
  const { t } = useLocoTranslation()

  const { data } = useAttachmentsPolicy(mailingId)

  return (
    <DisclosureBox
      defaultOpen
      wrapperClassName={twJoin(inModal && 'border border-text-secondary/30 rounded-lg')}
      title={
        <b className={`text-text-primary text-start text-[17px]`}>
          {t('dashboard.email.attachments')}
        </b>
      }
    >
      <div className={`flex flex-col gap-5`}>
        {data?.willBeSentAsLinks && (
          <SectionMessage
            textClassName={'text-brand-primary'}
            iconClassName={'fill-brand-primary'}
            className={'border-brand-primary bg-brand-primary/10'}
          >
            {t('dashboard.email.attachments_as_links')}
          </SectionMessage>
        )}
        <FileUploaderWithListedFiles
          source={FileSourceEnum.Email}
          files={attachmentsTempState.map(item => ({
            ...item.file,
            ...('id' in item ? { id: item.id } : {}),
          }))}
          onSelect={file => {
            setAttachmentsTempState(prev => concatAttachments(prev, file))
          }}
          onDelete={file => {
            setAttachmentsTempState(getFilteredAttachmentsList(attachmentsTempState, file))
          }}
          isPreFetching={isPreFetching}
        />
        {errors && <FieldErrorAndDescription error={errors} />}
      </div>
    </DisclosureBox>
  )
}

export default AttachmentsBlock
